import { Box, Paper } from "@mui/material";
import { ComponentProps, ReactNode } from "react";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import VisiblyScrollableV2 from "@/app/features/shared/__to_be_integrated/ui/v2/VisiblyScrollable";
import { alpha } from "@mui/material/styles";
import clsx from "clsx";
import styles from "./FlyoutWrapper.module.css";

export default function FlyoutWrapper({
    children,
    backgroundImage,
    backgroundComponent,
    contentSide = 'left',
    fit = 'fit',
    paperOpacity = 0.9,
    hideBackgroundOnMobile = true,
    mobileScrollMode = 'wrapper',
    slots
}: {
    children: ReactNode,
    backgroundImage?: MediaRef,
    backgroundComponent?: ReactNode,
    contentSide?: 'left' | 'right' | 'row-reverse',
    fit?: 'fit' | 'full' | 'small',
    paperOpacity?: number,
    hideBackgroundOnMobile?: boolean,
    mobileScrollMode?: 'wrapper' | 'none',
    slots?: {
        paper?: ComponentProps<typeof Paper>
    },
}) {
    const {
        className: slotPaperClassName,
        sx: slotPaperSx,
        ...slotPaperProps
    } = slots?.paper ?? {};

    return (
        <Box
            className={clsx("flyout-wrapper-root", styles.root)}
            sx={{
                contain: 'layout paint',
            }}
        >
            <Box
                className={clsx(
                    "flyout-wrapper-bg",
                    styles.bg,
                    hideBackgroundOnMobile && styles.bgHiddenOnMobile
                )}
                sx={{
                    willChange: 'transform',
                    transform: 'translateZ(0)',
                }}
            >
                {backgroundImage && <MediaView src={backgroundImage} fill />}
                {backgroundComponent}
            </Box>

            <Box
                className={clsx(
                    "flyout-wrapper-content",
                    styles.content,
                    contentSide === 'left'
                        ? styles.contentLeft
                        : (contentSide === 'right' ? styles.contentRight : styles.contentCenter)
                )}
            >
                <Paper
                    {...slotPaperProps}
                    className={clsx(
                        "flyout-wrapper-content-paper signature-border-radius",
                        styles.paper,
                        fit === 'full' && styles.paperFull,
                        slotPaperClassName
                    )}
                    sx={[
                        (theme) => ({
                            m: 2,
                            [theme.breakpoints.down('md')]: {
                                m: 0,
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                                width: '100%',
                                maxWidth: '100%',
                                ...(hideBackgroundOnMobile
                                    ? {
                                        height: 'calc(100dvh - var(--header-height, 132px))',
                                        maxHeight: 'calc(100dvh - var(--header-height, 132px))',
                                    }
                                    : {}),
                                p: 0,
                            },
                            backgroundColor: alpha(theme.palette.background.paper, paperOpacity),
                            willChange: 'transform, opacity',
                            transform: 'translateZ(0)',
                        }),
                        ...(Array.isArray(slotPaperSx) ? slotPaperSx : slotPaperSx ? [slotPaperSx] : [])
                    ]}
                    elevation={0}
                    autoFocus
                >
                    {mobileScrollMode === 'wrapper' ? (
                        <VisiblyScrollableV2
                            fadeSize="sm"
                            sx={{
                                paddingX: 2,
                                width: '100%',
                                flex: 1,
                                minHeight: 0,
                                maxHeight: '100%',
                            }}
                        >
                            {children}
                        </VisiblyScrollableV2>
                    ) : (
                        children
                    )}
                </Paper>
            </Box>
        </Box>
    )
}
